import { onlineManager } from '@tanstack/react-query';
import useDebounceFunction from '../../../../../../../../components/Hooks/useDebouncedFunction';
import { PackQuantitiesWithMultiplier } from '../../../../../../../../services/modules/product-colors-packs/product-colors-packs.types';
import { useLoadingStatusStore } from '../../../../../../../context/loadingStatus/LoadingStatus.store';
import useSharedEntities from '../../../../../../../shared-entities/hooks/useSharedEntities';
import useOrderProductGridContext from '../../../contexts/OrderProductGrid.context';
import {
  getSelectedPacksOnClickGridMultiply,
  getSelectedPacksOnClickRow,
} from '../utils/orderProductPacksMenu.utils';
import useHandleOrderProductPacks from './useHandleOrderProductPacks';
import { useSearchOrderProductPacks } from './useSearchOrderProductPacks';

const DEBOUNCE_TIME = 1200;

const useOrderProductPacksMenu = () => {
  const { isPublicAccess } = useSharedEntities({});
  const handleLoadingStatus = useLoadingStatusStore((state) => state.handleLoadingStatus);
  const {
    orderId,
    readMode,
    selectedPacks,
    handleSelectPacks,
    selectPacksSideEffect,
    onSelectPacksInstantAction,
    product,
  } = useOrderProductGridContext();

  const {
    options,
    loadMore,
    onSearch,
    isLoading,
    selectedData,
    onChangeSelectedData,
    onUpdateSelectedItem,
    isLoadingInitialValues,
  } = useSearchOrderProductPacks({
    readMode,
    selectedPacks,
    isOfflineMode: !onlineManager.isOnline(),
    productColorId: product.productColorId,
    productColorAvailability: product.availability,
    orderId,
  });

  const { updatePacks } = useHandleOrderProductPacks({
    orderId,
    productColorId: product.productColorId,
    availability: product.availability,
    deliveryWindowId: product.deliveryWindowId,
  });

  const { handleDebounceFunction: handleDebounceUpdatePacks } = useDebounceFunction({
    debounceTime: DEBOUNCE_TIME,
  });

  const handleUpdatePacks = async (packs: PackQuantitiesWithMultiplier[]) => {
    if (handleSelectPacks) {
      handleSelectPacks(packs);
      return;
    }
    const packsToUpdate = packs.map((pack) => ({
      id: pack.id,
      quantity: pack.multiplier ?? 0,
    }));
    await handleLoadingStatus({
      requestFn: () => updatePacks(packsToUpdate),
      onSuccess: () => {
        if (selectPacksSideEffect) {
          selectPacksSideEffect(packs);
        }
      },
    });
  };

  const onClickRow = (
    pack: PackQuantitiesWithMultiplier,
    packSelected: boolean,
    multiplier: number,
  ) => {
    if (readMode || isPublicAccess) {
      return;
    }
    if (onSelectPacksInstantAction) {
      onSelectPacksInstantAction();
    }
    onChangeSelectedData({ ...pack, multiplier });
    const selecteds = getSelectedPacksOnClickRow({ pack, multiplier, selectedData, packSelected });
    handleDebounceUpdatePacks(() => handleUpdatePacks(selecteds));
  };

  const onClickGridMultiply = (
    pack: PackQuantitiesWithMultiplier,
    packSelected: boolean,
    multiplier: number,
  ) => {
    if (readMode || isPublicAccess) {
      return;
    }
    if (onSelectPacksInstantAction) {
      onSelectPacksInstantAction();
    }
    const mustRemovePackItem = packSelected && multiplier === 0;
    const mustUpdatePackItem = packSelected && multiplier > 0;
    const selecteds = getSelectedPacksOnClickGridMultiply({
      pack,
      multiplier,
      selectedData,
      packSelected,
      mustRemovePackItem,
      mustUpdatePackItem,
    });
    if (!packSelected || mustRemovePackItem) {
      onChangeSelectedData({ ...pack, multiplier });
    } else {
      onUpdateSelectedItem({ ...pack, multiplier });
    }
    handleDebounceUpdatePacks(() => handleUpdatePacks(selecteds));
  };

  return {
    options,
    onSearch,
    loadMore,
    isLoading,
    onClickRow,
    selectedData,
    onClickGridMultiply,
    isLoadingInitialValues,
  };
};

export default useOrderProductPacksMenu;
